/**
 * A conclusão JÁ registrada de uma CCB, na mesa eletrônica (E74.F3).
 *
 * Metade em leitura da decisão do perito: o que ele decidiu, quando, com que
 * justificativa — e o botão de reabrir, que pede motivo.
 *
 * Separado do formulário (`DecisaoDoPerito`) porque são dois modos com regras
 * distintas: aqui não há validação nem rascunho, e lá não há histórico. Juntos
 * num componente só, a ramificação passava do limite de complexidade do lint.
 */
import { useState } from 'react';

import { Button, Field, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoRecordData } from '@/lib/exame_eletronico';

const t = ptBR.mesaEletronica;
const tq = ptBR.exameEletronico.quadro;

export function formatarInstante(iso: string | null): string {
  if (iso === null) return '';
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function Historico({ exame, idPrefix }: { exame: ExameEletronicoRecordData; idPrefix: string }) {
  const registros = exame.historico_reaberturas ?? [];
  if (registros.length === 0) return null;
  return (
    <div className="mt-sc-4" data-testid={`${idPrefix}-historico`}>
      <h5 className="text-caption text-fg-dim">{t.historicoTitulo}</h5>
      <ul className="mt-sc-1 space-y-sc-1 text-body-sm text-fg-muted">
        {registros.map((registro) => (
          <li key={`${registro.reaberto_em}-${registro.motivo}`}>
            {t.historicoItem
              .replace('{quando}', formatarInstante(registro.reaberto_em))
              .replace('{motivo}', registro.motivo)
              .replace(
                '{grau}',
                registro.grau_anterior === null ? '—' : tq.grauOpcoes[registro.grau_anterior],
              )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface ConclusaoRegistradaProps {
  exame: ExameEletronicoRecordData;
  idPrefix: string;
  reabrindoEmCurso: boolean;
  bloqueado: boolean;
  onReabrir: (motivo: string) => void;
}

export function ConclusaoRegistrada({
  exame,
  idPrefix,
  reabrindoEmCurso,
  bloqueado,
  onReabrir,
}: ConclusaoRegistradaProps): JSX.Element {
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const confirmar = (): void => {
    if (motivo.trim() === '') {
      setErro(t.reaberturaMotivoObrigatorio);
      return;
    }
    setErro(null);
    onReabrir(motivo.trim());
    setAberto(false);
    setMotivo('');
  };

  const grau = exame.grau_convergencia;
  const justificativa = exame.grau_justificativa ?? '';
  const observacoes = exame.observacoes_perito ?? '';

  return (
    <section className="mt-sc-6" data-testid={`${idPrefix}-decisao`} data-concluido="true">
      <h4 className="font-display text-[13px] uppercase tracking-[0.06em] text-fg-muted">
        {t.decisaoTitulo}
      </h4>
      <p className="mt-sc-2 text-body-sm text-fg-muted">
        {t.concluidaEm.replace('{quando}', formatarInstante(exame.concluido_em))}
      </p>
      <p className="mt-sc-1 text-body-sm text-fg" data-testid={`${idPrefix}-grau-final`}>
        {t.apuradoGrau.replace('{grau}', grau === null ? '—' : tq.grauOpcoes[grau])}
      </p>
      {/*
        E79.F1 — `break-words` nos três textos livres. A redação da conclusão
        carrega o código de verificação inteiro no meio da prosa, e 64
        caracteres sem espaço não cabem na coluna de decisão (24rem): com
        `pre-wrap` sozinho, que só quebra em espaço, o texto saía da tela.
        `break-words` e não `break-all` — prosa em PT-BR não pode quebrar no
        meio de palavra normal, só na sequência longa que não cabe.
      */}
      {justificativa !== '' ? (
        <p
          className="mt-sc-1 whitespace-pre-wrap break-words text-body-sm text-fg-muted"
          data-testid={`${idPrefix}-grau-justificativa`}
        >
          {justificativa}
        </p>
      ) : null}
      <p
        className="mt-sc-3 whitespace-pre-wrap break-words text-body-md text-fg"
        data-testid={`${idPrefix}-conclusao-texto`}
      >
        {exame.conclusao_texto}
      </p>
      {observacoes !== '' ? (
        <p
          className="mt-sc-3 whitespace-pre-wrap break-words text-body-sm text-fg-muted"
          data-testid={`${idPrefix}-observacoes-perito`}
        >
          {observacoes}
        </p>
      ) : null}

      <Historico exame={exame} idPrefix={idPrefix} />

      {aberto ? (
        <div className="mt-sc-4 border border-border p-sc-3" data-testid={`${idPrefix}-reabrir`}>
          <Field
            label={t.reaberturaMotivoLabel}
            htmlFor={`${idPrefix}-motivo`}
            error={erro ?? undefined}
          >
            <Textarea
              id={`${idPrefix}-motivo`}
              rows={2}
              value={motivo}
              placeholder={t.reaberturaMotivoPlaceholder}
              onChange={(event) => setMotivo(event.target.value)}
            />
          </Field>
          <div className="mt-sc-3 flex gap-sc-2">
            <Button
              type="button"
              onClick={confirmar}
              disabled={bloqueado}
              data-testid={`${idPrefix}-reabrir-confirmar`}
            >
              {reabrindoEmCurso ? t.reabrindo : t.reaberturaConfirmar}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAberto(false);
                setMotivo('');
                setErro(null);
              }}
              disabled={bloqueado}
            >
              {t.reaberturaCancelar}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="secondary"
          className="mt-sc-4"
          onClick={() => setAberto(true)}
          disabled={bloqueado}
          data-testid={`${idPrefix}-reabrir-abrir`}
        >
          {t.reabrir}
        </Button>
      )}
    </section>
  );
}
